import {
  CheckOutlined,
  LockOutlined,
  ProfileOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Col, notification, Row, Steps } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { COMPLETE_CLINICAL_USER_PROFILE } from '../../../apollo/clinicalUser';
import { COMPLETE_DOCTOR_PROFILE } from '../../../apollo/doctor';
import { COMPLETE_MANAGER_PROFILE } from '../../../apollo/manager';
import useEThreeInit from '../../../hooks/useEThreeInit';
import { Profile } from '../../../types/User';
import { redirectForRole } from '../../../utils';
import { ErrorLogger } from '../../../utils/ErrorLogger';
import { LocalStorage } from '../../../utils/LocalStorage';
import AuthForm from './AuthForm';
import DoctorForm from './DoctorForm';
import ProfileForm from './ProfileForm';
import {
  AuthType,
  DoctorType,
  ProfileType,
  StepItem,
  StepStatus,
  StepsType,
} from './types';

const steps: StepItem[] = [
  {
    icon: <LockOutlined />,
    title: 'Login information',
    key: 'auth',
  },
  {
    icon: <SolutionOutlined />,
    title: 'Profile Info',
    key: 'profile',
  },
  {
    icon: <ProfileOutlined />,
    title: 'RpmProvider Profile',
    key: 'profile',
  },
];

function getStepsStatus(
  currentStep: StepsType
): [StepStatus, StepStatus, StepStatus] {
  switch (currentStep) {
    case 'auth': {
      return ['process', 'wait', 'wait'];
    }
    case 'profile': {
      return ['finish', 'process', 'wait'];
    }
    case 'doctor': {
      return ['finish', 'finish', 'process'];
    }
    default: {
      return ['process', 'wait', 'wait'];
    }
  }
}

const Auth: AuthType = {
  email: '',
  password: '',
};

const ProfileData: Omit<Profile, 'id'> = {
  firstName: '',
  lastName: '',
  middleName: '',
  addressLine1: '',
  addressLine2: '',
  state: '',
  zipCode: '',
  phoneNumber: '',
  city: '',
};

const DoctorData = {
  specialization: '',
  bio: '',
};

const Payload = {
  auth: Auth,
  rpmProvider: DoctorData,
  profile: ProfileData,
};

const UserPayload = {
  auth: Auth,
  profile: ProfileData,
};

interface CompleteProfileProps {
  email: string;
  name: string;
  code: string;
  type: 'DOCTOR' | 'MANAGER' | 'CLINICAL_USER';
}

const CompleteProfile: FC<CompleteProfileProps> = ({
  email,
  name,
  code: invitationCode,
  type,
}) => {
  Payload.auth.email = email;
  const [step, setStep] = useState<StepsType>('auth');
  const { loading: e3Loading, eThreeInit } = useEThreeInit();
  const [firstName, lastName] = name.split(' ');
  const history = useHistory();
  const [completeProfileMutation, { data, loading }] = useMutation(
    COMPLETE_DOCTOR_PROFILE
  );

  const [
    completeManagerProfileMutation,
    { data: managerData, loading: completeManagerProfileLoading },
  ] = useMutation(COMPLETE_MANAGER_PROFILE);

  const [
    completeClinicalUserProfileMutation,
    { data: clinicalUserData, loading: completeClinicalUserProfileLoading },
  ] = useMutation(COMPLETE_CLINICAL_USER_PROFILE);

  useEffect(() => {
    const resp =
      data?.completeRpmProviderProfile ||
      managerData?.completeMPManagerInvitation ||
      clinicalUserData?.completeClinicalUserInvitation;
    const complete = async () => {
      const { token, role } = resp;

      LocalStorage.setItem('token', token);
      LocalStorage.setItem('role', role);

      await eThreeInit();

      redirectForRole(role, history);
    };

    if (resp) {
      complete();
    }
  }, [data, history, eThreeInit, managerData, clinicalUserData]);

  const completeProfile = async () => {
    const payload = Object.assign(Payload, { invitationCode });

    try {
      await completeProfileMutation({
        variables: {
          data: payload,
        },
      });

      notification.success({
        message: 'You have completed your profile successfully',
      });
    } catch (e) {
      ErrorLogger.logError(e);
    }
  };

  const completeManagerProfile = async () => {
    const payload = Object.assign(UserPayload, { invitationCode });

    try {
      await completeManagerProfileMutation({
        variables: {
          data: payload,
        },
      });

      notification.success({
        message: 'You have completed your profile successfully',
      });
    } catch (e) {
      ErrorLogger.logError(e);
    }
  };

  const completeClinicalUserProfile = async () => {
    const payload = Object.assign(UserPayload, { invitationCode });

    try {
      await completeClinicalUserProfileMutation({
        variables: {
          data: payload,
        },
      });

      notification.success({
        message: 'You have completed your profile successfully',
      });
    } catch (e) {
      ErrorLogger.logError(e);
    }
  };

  Payload.profile.firstName = Payload.profile.firstName || firstName;
  Payload.profile.lastName = Payload.profile.lastName || lastName;

  const onAuthSubmit = (values: AuthType) => {
    Payload.auth.email = values.email;
    Payload.auth.password = values.password;
    setStep('profile');
  };

  const onAuthBack = () => {
    setStep('auth');
  };

  const onProfileBack = () => {
    setStep('profile');
  };

  const onProfileSubmit = async (values: ProfileType) => {
    const { gender, ...rest } = values;

    Object.assign(Payload.profile, rest);

    if (type === 'MANAGER') {
      await completeManagerProfile();

      return;
    }

    if (type === 'CLINICAL_USER') {
      await completeClinicalUserProfile();

      return;
    }

    setStep('doctor');
  };

  const onComplete = async (values: DoctorType) => {
    Object.assign(Payload.rpmProvider, values);
    await completeProfile();
  };

  const renderSteps = () => {
    const stepsStatusesTuple = getStepsStatus(step);

    return steps.map((stepItem: StepItem, index) => {
      if ((type === 'MANAGER' || type === 'CLINICAL_USER') && index === 2)
        return;

      const status = stepsStatusesTuple[index];
      const icon = status === 'finish' ? <CheckOutlined /> : stepItem.icon;

      return (
        <Steps.Step
          key={stepItem.key}
          status={status}
          title={stepItem.title}
          icon={icon}
        />
      );
    });
  };

  const renderStepContent = () => {
    switch (step) {
      case 'auth': {
        return (
          <AuthForm initialValues={Payload.auth} onSubmit={onAuthSubmit} />
        );
      }
      case 'profile': {
        return (
          <ProfileForm
            initialValues={Payload.profile}
            onSubmit={onProfileSubmit}
            type={type}
            onBack={onAuthBack}
          />
        );
      }
      case 'doctor': {
        return (
          <DoctorForm
            initialValues={Payload.rpmProvider}
            onSubmit={onComplete}
            loading={
              loading ||
              e3Loading ||
              completeManagerProfileLoading ||
              completeClinicalUserProfileLoading
            }
            onBack={onProfileBack}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Row justify="center">
      <Col span="24">
        <Steps>{renderSteps()}</Steps>
        {renderStepContent()}
      </Col>
    </Row>
  );
};

export default CompleteProfile;
